<template>
  <div>
    <div
      class="select-wrap select-wrap--xs select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors }"
    >
      <select
        id="edit_profile_form_expected_date_of_birth_year"
        v-model="selectedYear"
        name="edit_profile_form[year_of_birth]"
        data-testid="year"
      >
        <option
          v-for="optionYear in optionYears"
          :key="optionYear"
          :value="optionYear"
          class="date_picker_year_option"
        >
          {{ optionYear }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs push-right--sm">年</p>
    <div
      class="select-wrap select-wrap--mini select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors }"
    >
      <select
        id="edit_profile_form_expected_date_of_birth_month"
        v-model="selectedMonth"
        name="edit_profile_form[year_of_birth]"
        data-testid="month"
      >
        <option
          v-for="optionMonth in optionMonths"
          :key="optionMonth"
          :value="optionMonth"
          class="date_picker_month_option"
        >
          {{ optionMonth }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs push-right--sm">月</p>
    <div
      class="select-wrap select-wrap--mini select-wrap--sp select-wrap-nomargin pull-left"
      :class="{ 'select-wrap-error': hasErrors }"
    >
      <select
        id="edit_profile_form_year_of_birth"
        v-model="selectedDay"
        name="edit_profile_form[year_of_birth]"
        data-testid="date"
      >
        <option
          v-for="dateOfMonth in optionDates"
          :key="dateOfMonth"
          :value="dateOfMonth"
          class="date_picker_date_option"
        >
          {{ dateOfMonth }}
        </option>
      </select>
    </div>
    <p class="select-text push-left--sm text--xs">日</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const EMPTY_OPTION = '--' as const;

type OptionValue = number | typeof EMPTY_OPTION;

const dateModel = defineModel<Date>('date', {
  default: () => new Date(),
});

const dayRef = ref(dateModel.value?.getDate() || EMPTY_OPTION);
const monthRef = ref(dateModel.value?.getMonth() + 1 || EMPTY_OPTION);
const yearRef = ref(dateModel.value?.getFullYear() || EMPTY_OPTION);

const selectedDay = computed({
  get: () => dayRef.value,
  set: (value: OptionValue) => {
    setDate({ day: value });
  },
});
const selectedMonth = computed({
  get: () => monthRef.value,
  set: (value: OptionValue) => {
    setDate({ month: value });
  },
});
const selectedYear = computed({
  get: () => yearRef.value,
  set: (value: OptionValue) => {
    setDate({ year: value });
  },
});

function setDate({
  year = yearRef.value,
  month = monthRef.value,
  day = dayRef.value,
}: {
  year?: OptionValue;
  month?: OptionValue;
  day?: OptionValue;
}) {
  let newDate = null;
  const isAllInputFilled =
    year !== EMPTY_OPTION && month !== EMPTY_OPTION && day !== EMPTY_OPTION;
  if (isAllInputFilled) {
    newDate = dayjs
      .utc() // startOf でローカルタイムゾーンになるため、 utc で固定する。
      .startOf('day')
      .year(year)
      .month(month - 1)
      .date(day)
      .toDate();
    dateModel.value = newDate;
  }
  dayRef.value = day;
  monthRef.value = month;
  yearRef.value = year;
}

const { yearsStartAgo, yearsEndIn } = defineProps({
  yearsStartAgo: { type: Number, required: false, default: 0 },
  yearsEndIn: { type: Number, required: false, default: 0 },
  hasErrors: { type: Boolean, required: false, default: false },
});

const optionDates = computed(() => {
  let dates = [];
  dates.push(EMPTY_OPTION);
  const lastDateOfMonth = calculateLastDateOfMonth();
  if (!lastDateOfMonth) {
    return dates;
  }
  for (let i of Array.from({ length: lastDateOfMonth }, (_, i) => i)) {
    dates.push(i + 1);
  }
  return dates;
});

function calculateLastDateOfMonth() {
  if (yearRef.value === EMPTY_OPTION || monthRef.value === EMPTY_OPTION) {
    return null;
  }
  let lastDateOfMonth = dayjs()
    .year(yearRef.value)
    .month(monthRef.value - 1)
    .endOf('month')
    .toDate();
  return lastDateOfMonth.getDate();
}

const optionMonths: OptionValue[] = [EMPTY_OPTION];
for (let i of Array.from({ length: 12 }, (_, k) => k)) {
  optionMonths.push(i + 1);
}

const optionYears = computed(() => {
  let years: OptionValue[] = [EMPTY_OPTION];
  // yearsStartAgo(基準日の何年前からoptionを用意するか)とyearsEndIn(何年後までpotionを用意するか)の合計分optionを生成する
  return years.concat(
    Array.from(
      // +1をしているのは、yearsStartAgoが0の場合に候補が全く表示されなくなるため
      { length: yearsStartAgo + yearsEndIn + 1 },
      (v, k) => dateModel.value.getFullYear() - yearsStartAgo + k,
    ),
  );
});
</script>
